import React, { useRef, useState, useEffect } from 'react';
import { Textarea, Box, Avatar, Text, Flex } from '@chakra-ui/react';
import { mutateFunction, useMutation } from '../../libs/apis';
import { useSelector } from 'react-redux';

// function AutoResizeTextarea({ placeholder, minRows = 1, ...props }) {
//   const textareaRef = useRef(null);
//   const [rows, setRows] = useState(minRows);

//   const handleInput = () => {
//     const textarea = textareaRef.current;
//     if (!textarea) return;

//     // Reset height to detect actual scrollHeight
//     textarea.style.height = 'auto';
//     // Calculate new height based on content
//     const newHeight = textarea.scrollHeight;
//     textarea.style.height = `${newHeight}px`;
//   };

//   useEffect(() => {
//     // Run once on mount to set initial height
//     handleInput();
//   }, []);

//   return (
//     <Textarea
//       ref={textareaRef}
//       rows={rows}
//       placeholder={placeholder}
//       onInput={handleInput}
//       resize="none"
//       {...props}
//     />
//   );
// }

// export default AutoResizeTextarea;

// import React, { useEffect, useRef, useState } from 'react';

export default function MentionTextarea({
  value,
  setValue = () => {},
  mentions,
  onMentionsChange,
  minRows = 1,
  onChange,
  ...props
}) {
  const textareaRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [caretPosition, setCaretPosition] = useState({ top: 0, left: 0 });
  const { mutate, isLoading } = useMutation(mutateFunction);
  const loggedInUserStatus = useSelector(
    (state) => state?.user?.userInfo?.status || '',
  );
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  const search = (inputValue, rect) => {
    if (inputValue !== '' && loggedInUserStatus !== 'disabled') {
      mutate(
        { key: `search-users?q=${inputValue}`, method: 'get' },
        {
          onSuccess(res) {
            setFilteredSuggestions(res.data.users);
            setShowSuggestions(true);
            setCaretPosition({
              top: rect.top + window.scrollY + textareaRef.current.offsetHeight,
              left: rect.left,
            });
          },
        },
      );
    }
  };

  // Resize logic
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;

    const caret = e.target.selectionStart;
    const textUntilCaret = newValue.slice(0, caret);
    const match = textUntilCaret.match(/@([\w]*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      setMentionQuery(query);
      const rect = e.target.getBoundingClientRect();

      search(query, rect);

      // Optional: Set suggestion box position (you can adjust later)
    } else {
      setShowSuggestions(false);
    }

    if (onChange) {
      onChange(e);
    }
  };

  const handleSuggestionClick = (user) => {
    const words = value.split(/\s/);
    const lastWord = words[words.length - 1];

    const tag = `@${user.first_name} ${user.last_name}`;
    if (lastWord.startsWith('@')) {
      words[words.length - 1] = tag;
    } else {
      words.push(`@${user.first_name} ${user.last_name}`);
    }

    const newText = words.join(' ');
    setValue(newText);
    onMentionsChange([...mentions, { id: user.id, tag }]);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1,
        );
      } else if (e.key === 'Enter' && showSuggestions) {
        e.preventDefault();
        handleSuggestionClick(filteredSuggestions[selectedSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        rows={1}
        maxRows={4}
        placeholder="What's on your mind?"
        // className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        minRows={minRows}
        {...props}
      />

      {showSuggestions && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          minWidth="220px"
          maxWidth="320px"
          width="100%"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          mt="2"
          overflowY="auto"
          // Remove right: 0;
        >
          {filteredSuggestions.map((user, index) => (
            <Flex
              key={user.id}
              p={3}
              cursor="pointer"
              bg={index === selectedSuggestionIndex ? 'blue.50' : 'transparent'}
              _hover={{ bg: 'blue.50' }}
              onClick={() => handleSuggestionClick(user)}
              alignItems="center"
              gap={3}
            >
              <Avatar
                size="sm"
                src={user.profile_image}
                name={`${user.first_name} ${user.last_name}`}
              />
              <Box flex="1">
                <Text fontWeight="medium" fontSize="sm">
                  {user.first_name} {user.last_name}
                </Text>
              </Box>
            </Flex>
          ))}
        </Box>
      )}
    </div>
  );
}
